import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { getEmbedConfig } from '../services/powerbiService';

const CanvasArea = ({ onReportReady }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  // Fetch embed token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const config = await getEmbedConfig();
        if (!cancelled) {
          setEmbedConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Power BI embed not available:', err.message);
          setError(err.message);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleReportLoad = useCallback((report) => {
    reportRef.current = report;
    if (onReportReady) onReportReady(report);
  }, [onReportReady]);

  const handleTokenExpiration = useCallback(async () => {
    try {
      const newConfig = await getEmbedConfig();
      setEmbedConfig(newConfig);
      if (reportRef.current) {
        await reportRef.current.setAccessToken(newConfig.token);
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
  }, []);

  if (embedConfig && !error) {
    return (
      <div className="canvas-area">
        <div className="canvas-frame">
          <PowerBIEmbed
            embedConfig={{
              type: 'report',
              id: embedConfig.reportId,
              embedUrl: embedConfig.embedUrl,
              accessToken: embedConfig.token,
              tokenType: models.TokenType.Embed,
              settings: {
                panes: {
                  filters:        { expanded: false, visible: false },
                  pageNavigation: { visible: false },
                },
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.Custom,
                customLayout: { displayOption: models.DisplayOption.FitToPage },
                navContentPaneEnabled: false,
              },
            }}
            eventHandlers={
              new Map([
                ['loaded',        () => console.log('PBI report loaded')],
                ['rendered',      () => console.log('PBI report rendered')],
                ['error',         (event) => console.error('PBI Error:', event.detail)],
                ['tokenExpiring', handleTokenExpiration],
              ])
            }
            cssClassName="pbi-embed-container"
            getEmbeddedComponent={(embedded) => handleReportLoad(embedded)}
          />
        </div>
      </div>
    );
  }

  // Fallback placeholder — backend not running or env not configured
  return (
    <div className="canvas-area">
      <div className="canvas-frame">
        <div className="canvas-placeholder">
          <h3>Power BI canvas</h3>
          {loading ? (
            <p>Connecting to Power BI…</p>
          ) : (
            <>
              <p>The embedded report will render here once the backend proxy is running and <code>.env</code> is configured.</p>
              <ol className="canvas-setup-steps">
                <li>Fill in <code>.env</code> with your Azure AD &amp; Power BI IDs</li>
                <li>Run <code>npm run server</code> to start the embed proxy</li>
                <li>Refresh — the report will load automatically</li>
              </ol>
              <div className="canvas-placeholder-badge">Power BI Embedded — awaiting configuration</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CanvasArea;
